import { useEffect, useState } from "react";
import { ArrowLeft } from "lucide-react";
import {
  applyFieldResourceDefaults,
  builtinFieldPresets,
  browserLocale,
  mapVariableType,
  matchVariableType,
  type FieldPreset,
  fieldTypes,
  newField,
  type Field,
  type Project,
} from "@proof/core";
import { Select, Button, Control, Modal, Toggle } from "./components";
import { fieldKeyFromLabel, fieldTypeInfo } from "./field-types";
import { useAuthoringServices } from "./services";

const choiceTypes: Field["type"][] = [
  "dropdown",
  "combobox",
  "radio",
  "checkbox",
];

/** Two short steps: what kind of information, then how it is asked. */
export function AddFieldDialog({
  project,
  onAdd,
  onClose,
}: {
  project: Project;
  onAdd: (field: Field) => void;
  onClose: () => void;
}) {
  const services = useAuthoringServices();
  const [mappings, setMappings] = useState<FieldPreset[]>(
    builtinFieldPresets(),
  );
  const [useMapping, setUseMapping] = useState(true);
  const [mappingError, setMappingError] = useState("");
  useEffect(() => {
    let active = true;
    if (services.listFieldPresets) {
      setMappingError("Loading variable mappings…");
      services
        .listFieldPresets({ kind: "variable-type" })
        .then((c) => {
          if (active) {
            setMappings(c.presets);
            setMappingError("");
          }
        })
        .catch(() => {
          if (active)
            setMappingError(
              "Variable mappings unavailable. Choose a type or turn off name mapping.",
            );
        });
    }
    return () => {
      active = false;
    };
  }, [services.listFieldPresets]);
  const [type, setType] = useState<Field["type"]>(),
    [label, setLabel] = useState(""),
    [key, setKey] = useState(""),
    [keyEdited, setKeyEdited] = useState(false),
    [required, setRequired] = useState(false),
    [help, setHelp] = useState(""),
    [options, setOptions] = useState(""),
    [datasetId, setDatasetId] = useState(""),
    [groupId, setGroupId] = useState(project.groups[0]?.id || "");
  const groups = [...new Set(fieldTypes.map((t) => fieldTypeInfo[t].group))];
  const token = keyEdited ? key : fieldKeyFromLabel(label, project);
  const keyTaken = project.fields.some((f) => f.key === token);
  const create = () => {
    if (
      !type ||
      !label.trim() ||
      !token ||
      keyTaken ||
      (choiceTypes.includes(type) && !datasetId && !options.trim()) ||
      (type === "text" && useMapping && mappingError)
    )
      return;
    const choices = choiceTypes.includes(type)
      ? options
          .split("\n")
          .map((line) => line.trim())
          .filter(Boolean)
          .map((line) => ({ label: line, value: line }))
      : undefined;
    onAdd(
      applyFieldResourceDefaults(
        project,
        ((field) =>
          type === "text" && useMapping
            ? mapVariableType(field, mappings)
            : field)({
          ...newField(type, project.fields.length),
          // New fields format with each viewer's browser locale.
          format: {
            ...newField(type, project.fields.length).format,
            locale: browserLocale,
          },
          label: label.trim(),
          key: token,
          required,
          help,
          ...(groupId ? { groupId } : {}),
          ...(datasetId
            ? {
                source: {
                  kind: "dataset" as const,
                  datasetId,
                  datasetVersion: project.datasets
                    .filter((dataset) => dataset.id === datasetId)
                    .sort((a, b) => b.version - a.version)[0]?.version,
                },
              }
            : choices
              ? { choices }
              : {}),
          ...(type === "checkbox" ? { default: [] } : {}),
        }),
      ),
    );
  };
  return (
    <Modal title="Add a field" onClose={onClose} wide>
      {!type ? (
        <div className="add-field-types">
          <p className="muted">What kind of information should people enter?</p>
          {groups.map((group) => (
            <section key={group} aria-label={group}>
              <h3>{group}</h3>
              <div className="add-field-type-grid">
                {fieldTypes
                  .filter((t) => fieldTypeInfo[t].group === group)
                  .map((t) => (
                    <button
                      key={t}
                      type="button"
                      className="add-field-type"
                      onClick={() => setType(t)}
                    >
                      <span className="field-type-icon" aria-hidden="true">
                        {fieldTypeInfo[t].icon}
                      </span>
                      <span>
                        <strong>{fieldTypeInfo[t].name}</strong>
                        <small>{fieldTypeInfo[t].description}</small>
                      </span>
                    </button>
                  ))}
              </div>
            </section>
          ))}
        </div>
      ) : (
        <form
          className="add-field-details"
          onSubmit={(e) => {
            e.preventDefault();
            create();
          }}
        >
          <button
            type="button"
            className="text-button"
            onClick={() => setType(undefined)}
          >
            <ArrowLeft size={13} /> {fieldTypeInfo[type].name} · change
          </button>
          <Control
            label="Label"
            note="The question or name people see on the form."
          >
            <input
              autoFocus
              value={label}
              placeholder="e.g. Full name"
              onChange={(e) => setLabel(e.target.value)}
            />
          </Control>
          {choiceTypes.includes(type) && (
            <Control label="Choice source">
              <Select
                value={datasetId}
                onChange={(event) => setDatasetId(event.target.value)}
              >
                <option value="">Enter options</option>
                {project.datasets
                  .filter(
                    (dataset) =>
                      !project.datasets.some(
                        (other) =>
                          other.id === dataset.id &&
                          other.version > dataset.version,
                      ),
                  )
                  .map((dataset) => (
                    <option key={dataset.id} value={dataset.id}>
                      {dataset.name}
                    </option>
                  ))}
              </Select>
            </Control>
          )}
          {choiceTypes.includes(type) && !datasetId && (
            <Control label="Options" note="One option per line.">
              <textarea
                rows={4}
                value={options}
                placeholder={"Small\nMedium\nLarge"}
                onChange={(e) => setOptions(e.target.value)}
              />
            </Control>
          )}
          {type === "text" && (
            <>
              <Toggle
                label="Use variable name mapping"
                value={useMapping}
                onChange={setUseMapping}
              />
              {useMapping && (
                <p>
                  {mappingError ||
                    (matchVariableType({ key: token, label }, mappings)
                      ? `Suggested type: ${fieldTypeInfo[matchVariableType({ key: token, label }, mappings)!.value.dataType].name}`
                      : "No name mapping; keep short text.")}
                </p>
              )}
            </>
          )}
          <Control label="Hint" note="Optional help shown with the field.">
            <input value={help} onChange={(e) => setHelp(e.target.value)} />
          </Control>
          {project.groups.length > 0 && (
            <Control
              label="Form section"
              note={
                groupId
                  ? undefined
                  : "Fields outside a section don't appear on this form until you place them."
              }
            >
              <Select
                value={groupId}
                onChange={(e) => setGroupId(e.target.value)}
              >
                {project.groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name || "Untitled section"}
                  </option>
                ))}
                <option value="">Not in a section</option>
              </Select>
            </Control>
          )}
          <Toggle label="Required" value={required} onChange={setRequired} />
          <details className="add-field-more">
            <summary>More options</summary>
            <Control
              label="Variable token"
              note={
                keyTaken
                  ? "Another field already uses this token."
                  : "The name designers use to place this value in the document."
              }
            >
              <input
                value={token}
                aria-invalid={keyTaken}
                onChange={(e) => {
                  setKeyEdited(true);
                  setKey(e.target.value.replace(/\s+/g, ""));
                }}
              />
            </Control>
          </details>
          <div className="button-row add-field-actions">
            <Button onClick={onClose}>Cancel</Button>
            <Button
              kind="primary"
              type="submit"
              disabled={
                !label.trim() ||
                !token ||
                keyTaken ||
                (choiceTypes.includes(type) && !datasetId && !options.trim()) ||
                (type === "text" && useMapping && !!mappingError)
              }
            >
              Add field
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
